"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

export const egnyteToolRegistry = {
  egnyteSearch: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteSearchToolView }) => ({
        default: EgnyteSearchToolView,
      }))
    ),
  },
  egnyteResolvePath: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteResolvePathToolView }) => ({
        default: EgnyteResolvePathToolView,
      }))
    ),
  },
  egnyteListFolder: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteListFolderToolView }) => ({
        default: EgnyteListFolderToolView,
      }))
    ),
  },
  egnyteGetFolderInfo: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteGetFolderInfoToolView }) => ({
        default: EgnyteGetFolderInfoToolView,
      }))
    ),
  },
  egnyteGetFileInfo: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteGetFileInfoToolView }) => ({
        default: EgnyteGetFileInfoToolView,
      }))
    ),
  },
  egnyteGetFileComments: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteGetFileCommentsToolView }) => ({
        default: EgnyteGetFileCommentsToolView,
      }))
    ),
  },
  egnyteDownloadFile: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ EgnyteDownloadFileToolView }) => ({
        default: EgnyteDownloadFileToolView,
      }))
    ),
  },
  egnyteUploadFile: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ EgnyteUploadFileToolView }) => ({
        default: EgnyteUploadFileToolView,
      }))
    ),
  },
  egnyteCreateFolder: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ EgnyteCreateFolderToolView }) => ({
        default: EgnyteCreateFolderToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant keyed by the progressive-disclosure inner-tool name
 * `egnyte_${toolName}`.
 *
 * Egnyte is a progressive-disclosure capability (ADR-057), so its tool calls
 * are dispatched through the `callCapabilityTool` proxy and every durable +
 * live-stream tool part is rewritten to the namespaced inner identity
 * `${capabilityId}_${toolName}` (e.g. `egnyte_egnyteListFolder`). The bare-name
 * entries above stay in the static tier (required by `AllToolNames` and used by
 * legacy persisted chats); these namespaced entries reuse the same lazy views
 * so the rich Egnyte cards render on the PD path instead of falling through to
 * the generic JSON fallback. Wired into the built-in registry in
 * `packages/sdk/react/src/tools/built-in-registry.ts`.
 */
export const egnyteProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(egnyteToolRegistry).map(([name, meta]) => [
    `egnyte_${name}`,
    meta,
  ])
);
